import moment from 'moment';

/**
 * Cross-field sync logic for the Move-node wizard's REMOVE/INSTALL prefill forms.
 *
 * `moveInstalledBaseNodePreview` computes both forms once, before the user types,
 * so the backend cannot react to mid-wizard edits. The invariant the frontend
 * must keep while the user edits:
 *
 *     installForm.pumpAgeAtInstallHours = priorRemovesTotal + removeForm.runningTimeHours
 *
 * where `priorRemovesTotal` (the asset's accumulated operating time from past
 * removals) is recoverable from the preview defaults:
 *
 *     priorRemovesTotal = pumpAgeAtInstall default − runningTime default
 *
 * Rules:
 * - removalDate change → recompute runningTime = floor(wholeDays × hoursPerDay)
 *   and cascade into pumpAgeAtInstall. The date always wins, even over a manual
 *   runningTime edit; a manual edit sticks until the next date change.
 * - manual runningTime change → cascade into pumpAgeAtInstall.
 * - manual pumpAgeAtInstall edit → latch: auto-cascade stops for the session.
 * - installationDate has no effect on any other field.
 *
 * Everything here is a pure function so the decisions stay unit-testable
 * without React (tests run under --env=node).
 */

// Wire-protocol attribute indices of the linked fields. The first two also
// appear in NUMBER_WITH_UNIT_ATTRIBUTE_INDICES (NumberWithUnitInput) for the
// Days/Hours display toggle — the stored values are always hours.
export const RUNNING_TIME_INDEX = 'runningTimeHours';
export const PUMP_AGE_INDEX = 'pumpAgeAtInstallHours';
export const REMOVAL_DATE_INDEX = 'removalDate';

interface AttributeLike {
    index?: string | null;
    value?: unknown;
}

/** Coerces a TDA value (the backend may send numbers as strings) to a finite number, else null. */
export const toFiniteNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

export const findAttributeValue = (
    attrs: AttributeLike[] | null | undefined,
    index: string
): unknown => attrs?.find((a) => a?.index === index)?.value;

export const hasAttributeIndex = (
    attrs: AttributeLike[] | null | undefined,
    index: string
): boolean => !!attrs?.some((a) => a?.index === index);

/**
 * The asset's accumulated operating time from past removals, derived from the
 * preview *defaults* (attribute values, not edited form state). Constant for a
 * wizard session; the preview seeds pumpAge = prior + currentRunningTime, so the
 * difference recovers `prior` without an extra query.
 */
export const computePriorRemovesTotal = (
    removeAttrs: AttributeLike[] | null | undefined,
    installAttrs: AttributeLike[] | null | undefined
): number => {
    const pumpAgeDefault = toFiniteNumber(findAttributeValue(installAttrs, PUMP_AGE_INDEX)) ?? 0;
    const runningTimeDefault = toFiniteNumber(findAttributeValue(removeAttrs, RUNNING_TIME_INDEX)) ?? 0;
    return Math.max(0, pumpAgeDefault - runningTimeDefault);
};

/**
 * Mirrors the backend's AssetLocation.runtimeHours formula: whole days between
 * installation and removal, scaled by averageRuntimePerDay and floored. Returns
 * null when a date is missing/invalid or the average is not a usable number —
 * callers must then leave runningTime untouched.
 */
export const computeRunningTimeFromDates = (
    installationDate: string | null | undefined,
    removalDate: string | null | undefined,
    averageRuntimePerDay: number | null | undefined
): number | null => {
    const avg = toFiniteNumber(averageRuntimePerDay);
    if (avg === null || avg < 0) return null;
    if (!installationDate || !removalDate) return null;
    const install = moment(installationDate);
    const removal = moment(removalDate);
    if (!install.isValid() || !removal.isValid()) return null;
    const wholeDays = Math.max(0, removal.startOf('day').diff(install.startOf('day'), 'days'));
    return Math.floor(wholeDays * avg);
};

/** pumpAgeAtInstall = everything the pump has run before it starts running at the new position. */
export const computePumpAgeAtInstall = (
    priorRemovesTotal: number,
    runningTimeHours: number | null
): number | null => {
    if (runningTimeHours === null) return null;
    return Math.max(0, priorRemovesTotal + runningTimeHours);
};

export interface RemoveSyncContext {
    priorRemovesTotal: number;
    /** assetLocation.installationDate of the source position; null for non-asset nodes. */
    sourceInstallDate: string | null;
    /** assetLocation.averageRuntimePerDay (the backend's resolved hoursPerDay); null when unavailable. */
    averageRuntimePerDay: number | null;
    /** The user manually overrode pumpAgeAtInstall — auto-cascade is off for the session. */
    pumpAgeLatched: boolean;
    /** The install form exists and contains the pumpAgeAtInstall attribute. */
    cascadeEnabled: boolean;
    /** The remove form contains the runningTime attribute. */
    runningTimeEnabled: boolean;
}

export interface RemoveSyncDecision {
    /** New runningTimeHours to write into the remove form (live, or deferred to submit). */
    nextRunningTime?: number;
    /** New pumpAgeAtInstallHours to stash for the install form. */
    nextPumpAge?: number;
}

// Dates are compared as normalized strings; a cleared date ('' from the form) equals null.
const normalizeDate = (value: unknown): string | null =>
    value === null || value === undefined || value === '' ? null : String(value);

/**
 * Decides what a remove-form values change implies. `prev` is the accumulated
 * previously-seen values (null = nothing seen yet: the first fire is the mount
 * echo carrying the BE defaults and must not trigger anything — the default
 * runningTime is the backend's estimate, not the date formula's result).
 * `next` may be a single step's slice; only keys present in it are diffed.
 */
export const evaluateRemoveValuesChange = (
    prev: Record<string, any> | null,
    next: Record<string, any>,
    ctx: RemoveSyncContext
): RemoveSyncDecision => {
    if (prev === null) return {};

    const decision: RemoveSyncDecision = {};
    // undefined = runningTime unchanged; null = cleared by the user.
    let effectiveRunningTime: number | null | undefined;

    const nextDate = normalizeDate(next[REMOVAL_DATE_INDEX]);
    const dateChanged =
        REMOVAL_DATE_INDEX in next && nextDate !== normalizeDate(prev[REMOVAL_DATE_INDEX]);

    if (dateChanged && nextDate !== null) {
        const recomputed = computeRunningTimeFromDates(
            ctx.sourceInstallDate,
            nextDate,
            ctx.averageRuntimePerDay
        );
        if (recomputed !== null && ctx.runningTimeEnabled) {
            // Date wins: overwrite even a manually edited runningTime.
            decision.nextRunningTime = recomputed;
            effectiveRunningTime = recomputed;
        }
    }

    if (effectiveRunningTime === undefined && RUNNING_TIME_INDEX in next) {
        const nextRt = toFiniteNumber(next[RUNNING_TIME_INDEX]);
        if (nextRt !== toFiniteNumber(prev[RUNNING_TIME_INDEX])) {
            effectiveRunningTime = nextRt;
        }
    }

    if (
        effectiveRunningTime !== undefined &&
        effectiveRunningTime !== null &&
        ctx.cascadeEnabled &&
        !ctx.pumpAgeLatched
    ) {
        const pumpAge = computePumpAgeAtInstall(ctx.priorRemovesTotal, effectiveRunningTime);
        if (pumpAge !== null) decision.nextPumpAge = pumpAge;
    }

    return decision;
};

/**
 * Authoritative sync point at remove-form submit: applies a date-derived
 * runningTime that could not be live-written (its field lives on a step that
 * was never mounted) and re-derives the pump age from the final runningTime.
 * Never mutates `merged`.
 */
export const finalizeRemoveSubmit = (
    merged: Record<string, any>,
    pendingRunningTime: number | null,
    ctx: RemoveSyncContext
): { mergedValues: Record<string, any>; pumpAgeWrite?: number } => {
    const mergedValues =
        pendingRunningTime !== null && ctx.runningTimeEnabled
            ? { ...merged, [RUNNING_TIME_INDEX]: pendingRunningTime }
            : { ...merged };

    if (!ctx.cascadeEnabled || ctx.pumpAgeLatched) return { mergedValues };

    const finalRunningTime = toFiniteNumber(mergedValues[RUNNING_TIME_INDEX]);
    if (finalRunningTime === null) return { mergedValues };

    const pumpAgeWrite = computePumpAgeAtInstall(ctx.priorRemovesTotal, finalRunningTime);
    return pumpAgeWrite === null ? { mergedValues } : { mergedValues, pumpAgeWrite };
};

/**
 * Detects a manual pumpAgeAtInstall edit on the install form. `lastAutoValue`
 * is the value the wizard last wrote (or the BE default before any cascade);
 * `undefined` means tracking is not initialized yet — never latch then. The
 * install form's mount echo carries the seeded value and therefore compares
 * equal; clearing the field counts as a manual edit.
 */
export const isManualPumpAgeEdit = (
    nextValue: unknown,
    lastAutoValue: number | null | undefined
): boolean => {
    if (lastAutoValue === undefined) return false;
    return toFiniteNumber(nextValue) !== lastAutoValue;
};
